import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CustomerVesselProvider } from './contexts/CustomerVesselContext';
import { LocationProvider } from './contexts/LocationContext';
import { Box, Typography, Button } from '@mui/material';
import { isAuthSessionValid, enforceAuthForProtectedRoutes, isProtectedPath } from './utils/auth';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ErrorBoundary from './components/ErrorBoundary';
import MarinaSettingsPage from './pages/admin/MarinaSettingsPage';
import PaymentSettingsPage from './pages/admin/PaymentSettingsPage';
import PoliciesPage from './pages/admin/PoliciesPage';
import SlipManagementPage from './pages/admin/marina/SlipManagementPage';
import AdminLayout from './layouts/MarinaAdminLayout';
import BusinessAdminLayout from './layouts/BusinessAdminLayout';
import MarketingRoutes from './routes/marketingRoutes';
import Unsubscribe from './pages/marketing/Unsubscribe';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import PasscodeVerificationPage from './pages/auth/PasscodeVerificationPage';
import LaunchRampAuthPage from './pages/auth/LaunchRampAuthPage';
import CustomerDashboard from './pages/customer/Dashboard';
import VesselListingPage from './pages/VesselListingPage';
import VesselDetailPage from './pages/VesselDetailPage';
import ReservationPage from './pages/ReservationPage';
import NotFoundPage from './pages/NotFoundPage';
import ReservationDetailsPage from './pages/ReservationDetailsPage';
import BookingPage from './pages/BookingPage';
import BusinessDashboard from './pages/admin/BusinessDashboard';
import LocationsPage from './pages/admin/business/LocationsPage';
import CustomersPage from './pages/admin/business/CustomersPage';
import DuplicateCustomersPage from './pages/admin/business/DuplicateCustomersPage';
import OverviewPage from './pages/admin/business/reports/OverviewPage';
import FinancialOverviewPage from './pages/admin/business/finance/OverviewPage';
import DocumentsPage from './pages/admin/business/DocumentsPage';
import SecuritySettingsPage from './pages/admin/business/settings/SecuritySettingsPage';
import GeneralSettingsPage from './pages/admin/business/settings/GeneralSettingsPage';
import PrintProxy from './pages/PrintProxy';
import ImageManagementPage from './pages/admin/business/settings/ImageManagementPage';
import TransactionalEmailTemplatesPage from './pages/admin/business/settings/TransactionalEmailTemplatesPage';
import StaffSchedulingPage from './pages/admin/business/StaffSchedulingPage';

// Staff pages
import StaffDashboard from './pages/staff/Dashboard';
import StaffReservationsPage from './pages/staff/ReservationsPage';
import StaffReservationDetailsPage from './pages/staff/ReservationDetailsPage';
import CheckInPage from './pages/staff/CheckInPage';
import LaunchRampPage from './pages/staff/LaunchRampPage';

// Store management pages
import StoreDashboard from './pages/staff/store/StoreDashboard';
import InventoryPage from './pages/staff/store/InventoryPage';
import LabelsPage from './pages/staff/store/LabelsPage';
import PricingPage from './pages/staff/store/PricingPage';
import ProductsListPage from './pages/staff/store/ProductsListPage';

// New static site pages
import AboutPage from './pages/AboutPage';
import MarinaParsed from './pages/MarinaParsed';
import CafePage from './pages/CafePage';
import StorePage from './pages/StorePage';
import ContactPage from './pages/ContactPage';
import WeatherPage from './pages/WeatherPage';
import WeathercamPage from './pages/WeathercamPage';
import JobsPage from './pages/JobsPage';

// Admin pages (preserved from existing app)
import AdminDashboard from './pages/admin/Dashboard';
import RegistrationsPage from './pages/admin/RegistrationsPage';
import AddVesselPage from './pages/admin/AddVesselPage';
import EditVesselPage from './pages/admin/EditVesselPage';
import VesselAvailabilityPage from './pages/admin/VesselAvailabilityPage';
import TrailerManagement from './pages/admin/TrailerManagement';
import StaffEditPage from './pages/admin/StaffEditPage';
import ReservationEditPage from './pages/staff/ReservationEditPage';
import PricingManagement from './pages/admin/PricingManagement';
import DiscountsPage from './pages/admin/DiscountsPage';
import AdminPricingPage from './pages/admin/PricingPage';
import KitchenDashboard from './pages/admin/kitchen/Dashboard';
import EmailSettingsPage from './pages/admin/EmailSettingsPage';
import StaffManagementPage from './pages/admin/business/StaffManagementPage';
import RoleManagementPage from './pages/admin/business/RoleManagementPage';
import SlipManagementWrapper from './pages/admin/marina/SlipManagementWrapper';
import ContractManagementPage from './pages/admin/marina/ContractManagementPage';
import SlipListView from './pages/admin/slips/SlipListView';
import WaitingListManagement from './pages/admin/WaitingListManagement';
import PoweredVesselsPage from './pages/admin/equipment/vessels/PoweredVesselsPage';
import NonPoweredVesselsPage from './pages/admin/equipment/vessels/NonPoweredVesselsPage';
import AccessoriesPage from './pages/admin/equipment/accessories/AccessoriesPage';
import AddAccessoryPage from './pages/admin/equipment/accessories/AddAccessoryPage';
import EditAccessoryPage from './pages/admin/equipment/accessories/EditAccessoryPage';
import EquipmentDashboard from './pages/admin/equipment/EquipmentDashboard';

// Import POS pages
import POSDashboard from './pages/pos/POSDashboard';
import RetailPOS from './pages/pos/RetailPOS';
import RestaurantPOS from './pages/pos/RestaurantPOS';
import OrderHistory from './pages/pos/OrderHistory';
import POSSettings from './pages/pos/POSSettings';
import UnifiedPOS from './pages/pos/UnifiedPOS';
import ProductsPage from './pages/pos/ProductsPage';

// Import CMS pages
import CMSDashboard from './pages/admin/business/online/CMSDashboard';
import PagesPage from './pages/admin/business/online/PagesPage';
import PageEditor from './pages/admin/business/online/PageEditor';
import TemplateManager from './pages/admin/business/online/TemplateManager';
import TemplateEditor from './pages/admin/business/online/TemplateEditor';
import TemplatePreview from './pages/admin/business/online/TemplatePreview';
import NavigationPage from './pages/admin/business/online/NavigationPage';
import ContentPage from './pages/admin/business/online/ContentPage';
import SiteSettingsPage from './pages/admin/business/online/SiteSettingsPage';
import CMSPageRenderer from './components/CMSPageRenderer';

// Components
import PrivateRoute from './components/PrivateRoute';
import ProtectedLaunchRampRoute from './components/ProtectedLaunchRampRoute';
import ProtectedStoreRoute from './components/ProtectedStoreRoute';
import StaffLayout from './layouts/StaffLayout';
import LaunchRampLayout from './layouts/LaunchRampLayout';
import StoreLayout from './layouts/StoreLayout';
import StoreAdminLayout from './layouts/StoreAdminLayout';
import KitchenAdminLayout from './layouts/StoreAdminLayout';
import DamageWaiverPage from './pages/admin/PoliciesPage';
import WaitingListForm from './pages/WaitingListForm';
import BookingFunnelDashboard from './components/admin/BookingFunnelDashboard';
import LaunchActivityPage from './pages/admin/marina/LaunchActivityPage';
import NewReservationPage from './pages/NewReservationPage';
import SessionRecordingsPage from './pages/admin/marina/SessionRecordingsPage';

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#4caf50',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: "'Montserrat', 'Roboto Slab', sans-serif",
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
  },
});

// Run immediate auth check before any component rendering
enforceAuthForProtectedRoutes();

// Create a component to check auth on every route change
const RouteChangeAuthChecker = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  
  useEffect(() => {
    // Only check auth on protected routes
    if (isProtectedPath(location.pathname)) {
      // Check if user is authenticated
      const checkAuth = async () => {
        try {
          const isValid = await isAuthSessionValid();
          
          if (!isValid && !currentUser) {
            window.location.href = `/auth?returnTo=${encodeURIComponent(location.pathname)}`;
          }
        } catch (error) {
          window.location.href = '/auth';
        }
      };
      
      checkAuth();
    }
  }, [location.pathname, currentUser]);
  
  return null; // This component doesn't render anything
};

function App() {
  // Special case: If URL is explicitly /staff/launch with auth=true param, bypass all routing
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const hasAuthParam = params.get('auth') === 'true';
    
    if (path === '/staff/launch' && hasAuthParam) {
      // Make sure localStorage is set
      localStorage.setItem('isPasscodeAuthenticated', 'true');
      
      // Remove the auth param from URL for cleanliness
      window.history.replaceState({}, '', '/staff/launch');
    }
    
    // Run auth check on route changes
    enforceAuthForProtectedRoutes();
  }, []);

  // Check if we're on the booking subdomain
  const isBookingSubdomain = window.location.hostname.startsWith('booking.') || 
                             window.location.hostname === 'booking-fllboats.web.app';

  // If we're on the booking subdomain, only show the booking page
  if (isBookingSubdomain) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
        <AuthProvider>
          <NotificationProvider>
            <Router>
              <Routes>
                <Route path="*" element={<BookingPage />} />
              </Routes>
            </Router>
          </NotificationProvider>
        </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <NotificationProvider>
            <CustomerVesselProvider>
              <LocationProvider>
                <div id="app-container">
                  <Router>
                    {/* Add the auth checker component */}
                    <RouteChangeAuthChecker />
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<HomePage />} />
                      <Route path="/login" element={<Navigate to="/auth" replace />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/passcode" element={<PasscodeVerificationPage />} />
                      <Route path="/auth" element={<LaunchRampAuthPage />} />
                      <Route path="/vessels" element={<VesselListingPage />} />
                      <Route path="/vessels/:id" element={<VesselDetailPage />} />
                      <Route path="/reservation/:vesselId" element={<ReservationPage />} />
                      <Route path="/booking" element={<BookingPage />} />
                      <Route path="/print-proxy" element={<PrintProxy />} />
                      <Route path="/waiting-list" element={<WaitingListForm />} />
                      
                      {/* Static site pages */}
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/marina" element={<MarinaParsed />} />
                      <Route path="/cafe" element={<CafePage />} />
                      <Route path="/store" element={<StorePage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/weather" element={<WeatherPage />} />
                      <Route path="/weathercam" element={<WeathercamPage />} />
                      <Route path="/jobs" element={<JobsPage />} />
                      
                      {/* Debug/fix auth session */}
                      <Route path="/fixauth" element={
                        <Box p={3}>
                          <Typography variant="h4" gutterBottom>Auth Session Fixed</Typography>
                          <Typography>
                            {(() => {
                              // Fix auth session
                              const newSession = {
                                authenticated: true,
                                timestamp: Date.now()
                              };
                              localStorage.setItem('authSession', JSON.stringify(newSession));
                              localStorage.setItem('isPasscodeAuthenticated', 'true');
                              return 'Authentication session has been reset and is now valid for 2 hours. You can now navigate to any protected page.';
                            })()}
                          </Typography>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => window.location.href = '/admin/business'} 
                            sx={{mt: 2}}
                          >
                            Go to Admin Dashboard
                          </Button>
                        </Box>
                      } />
                      
                      {/* DIRECT BYPASS ROUTE - No protection */}
                      <Route path="/launchramp" element={
                        <LaunchRampLayout>
                          <LaunchRampPage />
                        </LaunchRampLayout>
                      } />
                      
                      {/* Protected customer routes */}
                      <Route 
                        path="customer/*" 
                        element={
                          <PrivateRoute allowedRoles={['customer', 'staff', 'admin']}>
                            <Routes>
                              <Route path="dashboard" element={<CustomerDashboard />} />
                              <Route path="reservation/:vesselId" element={<ReservationPage />} />
                              <Route path="reservation-details/:id" element={<ReservationDetailsPage />} />
                              <Route path="*" element={<Navigate to="/customer/dashboard" replace />} />
                            </Routes>
                          </PrivateRoute>
                        } 
                      />
                      
                      {/* Protected staff routes */}
                      <Route 
                        path="staff/*" 
                        element={
                          <PrivateRoute allowedRoles={['staff', 'admin']}>
                            <CustomerVesselProvider>
                              <Routes>
                                {/* Launch ramp section with passcode protection */}
                                <Route path="launch" element={
                                  <ProtectedLaunchRampRoute>
                                    <LaunchRampLayout>
                                      <LaunchRampPage />
                                    </LaunchRampLayout>
                                  </ProtectedLaunchRampRoute>
                                } />
                                
                                {/* SPECIAL DIRECT ACCESS ROUTE - Bypasses protection completely */}
                                <Route path="launch/direct" element={
                                  <LaunchRampLayout>
                                    <LaunchRampPage />
                                  </LaunchRampLayout>
                                } />
                                
                                {/* Store management section with passcode protection */}
                                <Route path="store/*" element={
                                  <ProtectedStoreRoute>
                                    <StoreLayout>
                                      <Routes>
                                        <Route path="" element={<StoreDashboard />} />
                                        <Route path="products" element={<ProductsListPage />} />
                                        <Route path="inventory" element={<InventoryPage />} />
                                        <Route path="inventory/update" element={<InventoryPage />} />
                                        <Route path="inventory/scan" element={<InventoryPage />} />
                                        <Route path="labels" element={<LabelsPage />} />
                                        <Route path="pricing" element={<PricingPage />} />
                                        <Route path="*" element={<Navigate to="/staff/store" replace />} />
                                      </Routes>
                                    </StoreLayout>
                                  </ProtectedStoreRoute>
                                } />
                                
                                {/* Regular staff section */}
                                <Route path="dashboard" element={
                                  <StaffLayout>
                                    <StaffDashboard />
                                  </StaffLayout>
                                } />
                                <Route path="reservations" element={
                                  <StaffLayout>
                                    <StaffReservationsPage />
                                  </StaffLayout>
                                } />
                                <Route path="reservations/:id" element={
                                  <StaffLayout>
                                    <StaffReservationDetailsPage />
                                  </StaffLayout>
                                } />
                                <Route path="checkin" element={
                                  <StaffLayout>
                                    <CheckInPage />
                                  </StaffLayout>
                                } />
                                <Route path="launch-ramp" element={
                                  <StaffLayout>
                                    <LaunchRampPage />
                                  </StaffLayout>
                                } />
                                <Route path="new" element={
                                  <StaffLayout>
                                    <NewReservationPage />
                                  </StaffLayout>
                                } />
                                <Route path="reservations/:id/edit" element={
                                  <StaffLayout>
                                    <ReservationEditPage />
                                  </StaffLayout>
                                } />
                                <Route path="edit/:id" element={
                                  <StaffLayout>
                                    <ReservationEditPage />
                                  </StaffLayout>
                                } />
                                <Route path="slips" element={
                                  <StaffLayout>
                                    <SlipManagementPage />
                                  </StaffLayout>
                                } />
                                <Route path="*" element={<Navigate to="/staff/dashboard" replace />} />
                              </Routes>
                            </CustomerVesselProvider>
                          </PrivateRoute>
                        } 
                      />
                      
                      {/* Protected admin routes */}
                      <Route 
                        path="admin" 
                        element={<Navigate to="/admin/business" replace />} 
                      />
                      
                      {/* Business admin route */}
                      <Route 
                        path="admin/business/*" 
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <BusinessAdminLayout>
                              <Routes>
                                <Route path="" element={<BusinessDashboard />} />
                                <Route path="dashboard" element={<BusinessDashboard />} />
                                
                                {/* Customer Management */}
                                <Route path="customers" element={<CustomersPage />} />
                                <Route path="customers/duplicates" element={<DuplicateCustomersPage />} />
                                
                                {/* Staff Management */}
                                <Route path="staff" element={<StaffManagementPage />} />
                                <Route path="staff/new" element={<StaffEditPage />} />
                                <Route path="staff/edit/:id" element={<StaffEditPage />} />
                                <Route path="staff/roles" element={<RoleManagementPage />} />
                                <Route path="staff/scheduling" element={<StaffSchedulingPage />} />
                                
                                {/* Business Locations */}
                                <Route path="locations" element={<LocationsPage />} />
                                <Route path="locations/new" element={<LocationsPage />} />
                                <Route path="locations/edit/:id" element={<LocationsPage />} />
                                
                                {/* Reports */}
                                <Route path="reports/overview" element={<OverviewPage />} />
                                <Route path="reports/marina" element={<OverviewPage />} />
                                <Route path="reports/store" element={<OverviewPage />} />
                                <Route path="reports/kitchen" element={<OverviewPage />} />
                                
                                {/* Finance */}
                                <Route path="finance/overview" element={<FinancialOverviewPage />} />
                                <Route path="finance/accounting" element={<FinancialOverviewPage />} />
                                <Route path="finance/transactions" element={<FinancialOverviewPage />} />
                                
                                {/* Documents */}
                                <Route path="documents" element={<DocumentsPage />} />
                                
                                {/* Notifications */}
                                <Route path="notifications" element={<DocumentsPage />} />
                                
                                {/* Settings */}
                                <Route path="settings/general" element={<GeneralSettingsPage />} />
                                <Route path="settings/security" element={<SecuritySettingsPage />} />
                                <Route path="settings/email" element={<EmailSettingsPage />} />
                                <Route path="settings/marina" element={<SlipManagementPage />} />
                                <Route path="marina/contracts" element={<ContractManagementPage />} />
                                <Route path="slips" element={<SlipListView />} />
                                <Route path="settings/images" element={<ImageManagementPage />} />
                                <Route path="settings/transactional-email-templates" element={<TransactionalEmailTemplatesPage />} />
                                
                                {/* Marketing Module */}
                                <Route path="marketing/*" element={<MarketingRoutes />} />
                                
                                {/* Online/CMS Module */}
                                <Route path="online/dashboard" element={<CMSDashboard />} />
                                <Route path="online/pages" element={<PagesPage />} />
                                <Route path="online/pages/new" element={<PageEditor />} />
                                <Route path="online/pages/:id/edit" element={<PageEditor />} />
                                <Route path="online/templates" element={<TemplateManager />} />
                                <Route path="online/templates/new" element={<TemplateEditor />} />
                                <Route path="online/templates/:id/edit" element={<TemplateEditor />} />
                                <Route path="online/templates/:id/preview" element={<TemplatePreview />} />
                                <Route path="online/navigation" element={<NavigationPage />} />
                                <Route path="online/content" element={<ContentPage />} />
                                <Route path="online/site-settings" element={<SiteSettingsPage />} />
                                
                                <Route path="*" element={<Navigate to="/admin/business" replace />} />
                              </Routes>
                            </BusinessAdminLayout>
                          </PrivateRoute>
                        } 
                      />
                      
                      {/* Department-specific admin routes */}
                      <Route 
                        path="admin/*" 
                        element={
                          <PrivateRoute allowedRoles={['admin']}>
                            <AdminLayout>
                              <Routes>
                                <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
                                <Route path="dashboard" element={<AdminDashboard />} />
                                <Route path="booking-funnel" element={<BookingFunnelDashboard />} />
                                
                                {/* Store section */}
                                <Route path="store/dashboard" element={
                                  <StoreAdminLayout>
                                    <StoreDashboard />
                                  </StoreAdminLayout>
                                } />
                                
                                <Route path="store/products/list" element={<ProductsListPage />} />
                                
                                {/* Kitchen section */}
                                <Route path="kitchen/dashboard" element={
                                  <StoreAdminLayout>
                                    <KitchenDashboard />
                                  </StoreAdminLayout>
                                } />
                                
                                                                {/* Equipment section */}
                <Route path="equipment" element={<EquipmentDashboard />} />
                <Route path="equipment/vessels" element={<Navigate to="/admin/equipment/vessels/powered" replace />} />
                <Route path="equipment/vessels/powered" element={<PoweredVesselsPage />} />
                <Route path="equipment/vessels/nonpowered" element={<NonPoweredVesselsPage />} />
                <Route path="equipment/vessels/new" element={<AddVesselPage />} />
                <Route path="equipment/vessels/edit/:id" element={<EditVesselPage />} />
                <Route path="equipment/vessels/availability/:id" element={<VesselAvailabilityPage />} />
                <Route path="equipment/trailers" element={<TrailerManagement />} />
                <Route path="equipment/accessories" element={<AccessoriesPage />} />
                <Route path="equipment/accessories/new" element={<AddAccessoryPage />} />
                <Route path="equipment/accessories/edit/:id" element={<EditAccessoryPage />} />
                                {/* Staff section */}
                                <Route path="staff" element={<AdminDashboard />} />
                                <Route path="staff/new" element={<StaffEditPage />} />
                                <Route path="staff/edit/:id" element={<StaffEditPage />} />
                                {/* Pricing section */}
                                <Route path="pricing" element={<PricingManagement />} />
                                <Route path="pricing/discounts" element={<DiscountsPage />} />
                                <Route path="pricing/waiver" element={<PoliciesPage />} />
                                {/* Registrations section */}
                                <Route path="registrations" element={<RegistrationsPage />} />
                                {/* Settings */}
                                <Route path="settings" element={<MarinaSettingsPage />} />
                                <Route path="settings/payment" element={<PaymentSettingsPage />} />
                                <Route path="settings/policies" element={<PoliciesPage />} />
                                <Route path="settings/marina" element={<SlipManagementPage />} />
                                <Route path="marina/contracts" element={<ContractManagementPage />} />
                                <Route path="slips" element={<SlipListView />} />
                                <Route path="settings/images" element={<ImageManagementPage />} />
                                <Route path="settings/transactional-email-templates" element={<TransactionalEmailTemplatesPage />} />
                                <Route path="waiting-list" element={<WaitingListManagement />} />
                                <Route path="marina/launch-activity" element={<LaunchActivityPage />} />
                                <Route path="marina/session-recordings" element={<SessionRecordingsPage />} />
                                <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                              </Routes>
                            </AdminLayout>
                          </PrivateRoute>
                        } 
                      />
                      
                      {/* POS routes */}
                      <Route
                        path="pos/*"
                        element={
                          <PrivateRoute allowedRoles={['staff', 'admin']}>
                            <Routes>
                              <Route path="" element={<POSDashboard />} />
                              <Route path="retail" element={<RetailPOS />} />
                              <Route path="restaurant" element={<RestaurantPOS />} />
                              <Route path="unified" element={<UnifiedPOS />} />
                              <Route path="orders" element={<OrderHistory />} />
                              <Route path="products" element={<ProductsPage />} />
                              <Route path="settings" element={<POSSettings />} />
                              <Route path="*" element={<Navigate to="/pos" replace />} />
                            </Routes>
                          </PrivateRoute>
                        }
                      />
                      
                      {/* Print Proxy route */}
                      <Route path="/print-proxy" element={<PrintProxy />} />
                      
                      {/* Auth fix route */}
                      <Route path="/fixauth" element={
                        <Navigate to="/auth" state={{ fix: true }} replace />
                      } />
                      
                      {/* Dynamic CMS Pages - This catches any remaining unmatched routes */}
                      <Route path="/:slug" element={<CMSPageRenderer />} />
                      
                      {/* 404 routes */}
                      <Route path="/404" element={<NotFoundPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Router>
                </div>
              </LocationProvider>
            </CustomerVesselProvider>
          </NotificationProvider>
        </AuthProvider>
      </LocalizationProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
